import React, { useState, useEffect, useCallback } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { Sparkles, Save, Loader2, RotateCcw } from "lucide-react";

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const SEGMENT_LABELS = {
  todos: "Todos los usuarios",
  inactivos: "Usuarios inactivos",
  nuevos: "Usuarios nuevos sin primer pedido",
};

function Section({ title, desc, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <p className="font-bold text-gray-800 text-sm mb-1">{title}</p>
      {desc && <p className="text-xs text-gray-400 mb-3">{desc}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export default function MarketingAiConfig() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [anglesText, setAnglesText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/marketing/ai-config");
      const cfg = res.data?.data?.config;
      setConfig(cfg);
      setDefaults(res.data?.data?.defaults);
      setAnglesText((cfg?.creativeAngles ?? []).join("\n"));
    } catch { toast.error("Error cargando la configuración"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const creativeAngles = anglesText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (creativeAngles.length === 0) { toast.warn("Agrega al menos un ángulo creativo"); return; }
    setSaving(true);
    try {
      const res = await API.put("/marketing/ai-config", { ...config, creativeAngles });
      setConfig(res.data?.data?.config);
      toast.success("Configuración de IA actualizada");
    } catch (err) { toast.error(err.response?.data?.message || "Error guardando"); }
    finally { setSaving(false); }
  };

  const handleRestoreDefaults = () => {
    if (!defaults) return;
    if (!window.confirm("Esto reemplaza TODO lo de abajo (modelo, prompt, briefs, ángulos) por los valores de fábrica. ¿Continuar?")) return;
    setConfig(defaults);
    setAnglesText((defaults.creativeAngles ?? []).join("\n"));
    toast.info("Restaurado — todavía falta guardar");
  };

  if (loading || !config) {
    return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-supernova-500" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="text-supernova-600" size={26} /> IA de marketing
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Controla cómo Claude redacta las notificaciones de la parrilla diaria de marketing.
          </p>
        </div>
        <button onClick={handleRestoreDefaults} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs`}>
          <RotateCcw size={12} /> Restaurar defaults
        </button>
      </div>

      <Section title="Modelo y parámetros">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Modelo</label>
            <input className={inp} value={config.model} onChange={(e) => setConfig({ ...config, model: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Max tokens</label>
            <input type="number" className={inp} value={config.maxTokens} onChange={(e) => setConfig({ ...config, maxTokens: Number(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Temperature</label>
            <input type="number" step="0.1" min="0" max="2" className={inp} value={config.temperature} onChange={(e) => setConfig({ ...config, temperature: Number(e.target.value) })} />
          </div>
        </div>
      </Section>

      <Section title="System prompt" desc="Instrucciones generales de tono/formato que Claude sigue siempre, sin importar el segmento.">
        <textarea className={inp} rows={8} value={config.systemPrompt} onChange={(e) => setConfig({ ...config, systemPrompt: e.target.value })} />
      </Section>

      <Section title="Briefs por segmento" desc="Qué objetivo tiene el mensaje para cada tipo de usuario.">
        {Object.keys(SEGMENT_LABELS).map((seg) => (
          <div key={seg}>
            <label className="text-xs font-bold text-gray-500 mb-1 block">{SEGMENT_LABELS[seg]}</label>
            <textarea className={inp} rows={3} value={config.segmentBriefs[seg] ?? ""}
              onChange={(e) => setConfig({ ...config, segmentBriefs: { ...config.segmentBriefs, [seg]: e.target.value } })} />
          </div>
        ))}
      </Section>

      <Section title="Ángulos creativos" desc="Uno por línea — se elige uno al azar en cada mensaje para que no suenen repetitivos.">
        <textarea className={inp} rows={10} value={anglesText} onChange={(e) => setAnglesText(e.target.value)} />
      </Section>

      <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
        <Save size={14} /> {saving ? "Guardando…" : "Guardar configuración"}
      </button>
    </div>
  );
}
